import { Injectable, inject } from '@angular/core';
import {
  AssetColorLabel,
  AssetDto,
  AssetStatus,
  CommentPosition,
  CreateAssetCommentBody,
  GalleryDto,
  UpdateAssetBodyDto,
} from '@nusign/picflow-backend-types';
import { firstValueFrom, map, of, take } from 'rxjs';
import { ApprovalLimitReachedDialogComponent } from '../gallery-shared/dialogs/approval-limit-reached-dialog/approval-limit-reached-dialog.component';
import { AssetFeedback } from '../gallery-shared/interfaces/asset-feedback';
import { Shape } from '../gallery-shared/types/shapes';
import { convertShapesToAnnotation } from '../lib/convert-shapes';
import { getAnonymousGuestAvatar } from '../lib/create-avatar';
import { fixContentEditableStyles } from '../lib/fix-contenteditable-styles';
import { PICFLOW_ANALYTICS } from '../picflow-analytics';
import { GuestProfileDialogComponent } from '../view-gallery/dialogs/guest-profile-dialog/guest-profile-dialog.component';
import { AssetApprovalLimitService } from './asset-approval-limit.service';
import { AssetService } from './asset.service';
import { CommentService } from './comment.service';
import { DataLoaderService } from './data-loader.service';
import { DialogService } from './dialog.service';
import { GuestAuthService } from './guest-auth.service';
import { StateService } from './state.service';
import { StorageService } from './storage.service';
import { ToastService } from './toast.service';
import { UserService } from './user.service';
import { WatcherService } from './watcher.service';

@Injectable({
  providedIn: 'root',
})
export class AssetFeedbackService {
  private assetService = inject(AssetService);
  private watcherService = inject(WatcherService);
  private loader = inject(DataLoaderService);
  private state = inject(StateService);
  private userService = inject(UserService);
  private guestAuthService = inject(GuestAuthService);
  private commentService = inject(CommentService);
  private storage = inject(StorageService);
  private toasts = inject(ToastService);
  private assetApprovalLimitService = inject(AssetApprovalLimitService);
  private dialog = inject(DialogService);
  showedAnonymousInfo = false;
  private analytics = inject(PICFLOW_ANALYTICS);

  private async updateAsset(asset: AssetDto, gallery: GalleryDto, params: UpdateAssetBodyDto, isPublic: boolean) {
    await this.assetService.updateAsset(asset, params, asset.gallery as string, <string>asset.section, isPublic);
    this.afterFeedback(asset, gallery);
  }

  public canUpdateAssetStatus(asset: AssetDto, gallery: GalleryDto, status: AssetStatus) {
    if (!gallery.approval_limits) return of(true); // no limit
    if (status !== 'approved') return of(true);
    if (gallery.approval_limits < 0) return of(true); // soft limit
    return this.assetApprovalLimitService.getApprovedAssetCountForGallery(asset.gallery as string).pipe(
      take(1),
      map(count => {
        return count < Math.abs(gallery.approval_limits);
      })
    );
  }

  async updateAssetStatus(asset: AssetDto, gallery: GalleryDto, status: AssetStatus, isPublic: boolean) {
    if (!gallery.enabled_feedback_options?.includes('status')) return;
    if (!(await firstValueFrom(this.canUpdateAssetStatus(asset, gallery, status)))) {
      this.dialog.open(ApprovalLimitReachedDialogComponent, { gallery });
      return;
    }
    if (!(await this.checkAuth(gallery, { type: 'status', content: status }))) return;
    const _asset = { ...asset };
    asset.status = status;
    await this.updateAsset(_asset, gallery, { status }, isPublic);
  }

  async updateColorLabel(asset: AssetDto, gallery: GalleryDto, color_label: AssetColorLabel, isPublic: boolean) {
    if (!gallery.enabled_feedback_options?.includes('color_label')) return;
    if (!(await this.checkAuth(gallery, { type: 'color_label', content: color_label }))) return;
    const _asset = { ...asset };
    asset.color_label = color_label;
    await this.updateAsset(_asset, gallery, { color_label }, isPublic);
  }

  async toggleLike(asset: AssetDto, gallery: GalleryDto) {
    if (!gallery.enabled_feedback_options?.includes('favourite')) return;
    const wasLoggedIn = !!this.state.guestAuthToken || !!this.state.user;
    if (!(await this.checkAuth(gallery, { type: 'like' }))) return;

    let _asset = asset;
    if (!wasLoggedIn) {
      this.loader.removeFuzzy('assets_');
      _asset = await this.assetService.getAsset(asset.id, gallery.id, { noCache: true });
      asset['is_liked'] = _asset['is_liked'];
    }

    if (_asset['is_liked'] && !wasLoggedIn) return; // was already liked

    _asset['is_liked'] = !_asset['is_liked'];
    asset['is_liked'] = _asset['is_liked'];
    if (_asset['is_liked']) {
      _asset.likes += 1;
      if (asset !== _asset) {
        asset.likes += 1;
      }
    } else {
      _asset.likes -= 1;
      if (asset !== _asset) {
        asset.likes -= 1;
      }
    }

    await this.assetService.toggleAssetLike(asset, gallery.id, _asset['is_liked']);
    this.afterFeedback(asset, gallery);
  }

  async sendComment(
    asset: AssetDto,
    gallery: GalleryDto,
    params: Partial<CreateAssetCommentBody>,
    annotation: Shape[],
    position?: CommentPosition
  ) {
    this.storage.set('annotation', annotation, true);
    if (!params.text && !annotation.length) return;

    // trimming <br> at the start and end of the text
    if (params.text) {
      params.text = fixContentEditableStyles(params.text.replace(/<br>/g, '\n').trim().replace(/\n/g, '<br>'));
    }

    if (!(await this.checkAuth(gallery, { type: 'comment', content: params.text }))) return;

    params.annotation = convertShapesToAnnotation(annotation);
    params.position = position;
    if (!params.text) delete params.text;

    this.analytics.track('comment_added', false, {
      annotation: !!params.annotation,
      text: !!params.text,
      reply: false,
      private: !!params.private,
    });

    const comment = await firstValueFrom(this.commentService.postComment(asset.id, gallery.id, params));

    this.afterFeedback(asset, gallery);
    return comment;
  }

  public async checkAuth(gallery: GalleryDto, feedback?: AssetFeedback) {
    // Check if identified
    const guestLoggedIn = !!this.state.guestAuthToken;
    const userLoggedIn = !!(await firstValueFrom(this.userService.getUser()));

    if (!guestLoggedIn && !userLoggedIn) {
      const resp = await this.guestAuthService.authenticateForFeedback(gallery, feedback);
      if (!resp) return;
    }
    return true;
  }

  private async afterFeedback(asset: AssetDto, gallery: GalleryDto) {
    this.autoWatchAsset(asset, gallery);
    if (this.state.guest && !this.state.guest.email) {
      if (this.showedAnonymousInfo) return;
      const avatar = getAnonymousGuestAvatar(this.state.guest.anonymous_id);
      this.showedAnonymousInfo = true;
      this.toasts.add(`You just left feedback as: <b>${this.state.guest.name}</b>`, 'custom', {
        duration: 5000,
        customSvg: avatar,
        imageSize: 48,
        cta: {
          icon: '/assets/icons/chevron-right.svg',
          color: 'simple',
          height: '1',
          onClick: () => {
            this.dialog.open(GuestProfileDialogComponent, { gallery });
          },
        },
      });
    }
  }

  private async autoWatchAsset(asset: AssetDto, gallery: GalleryDto) {
    if (gallery.notifications_enabled === false) return;
    this.watcherService.watchAsset$.next({ assetId: asset.id, gallery });
  }
}
